import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Coins, TrendingUp, Flame, Gift, Star, ChevronRight,
  CalendarDays, Award, Clock, Zap, ArrowUpRight, ArrowDownRight, Loader2
} from "lucide-react";

const EVENT_ICONS = {
  booking_complete: CalendarDays,
  first_booking: Gift,
  weekly_streak: Flame,
  referral_activated: Star,
  event_attendance: Zap,
  review_submitted: Award,
  checkin_bonus: Clock,
  founding_daily: Award,
};
const EVENT_COLORS = {
  booking_complete: "text-green-600",
  first_booking: "text-amber-500",
  weekly_streak: "text-orange-500",
  founding_daily: "text-amber-600",
  referral_activated: "text-blue-600",
  event_attendance: "text-purple-600",
};

export default function PlayWallet() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [rules, setRules] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/play/summary"),
      api.get("/play/rules"),
      api.get("/play/leaderboard"),
    ]).then(([s, r, l]) => {
      setSummary(s.data);
      setRules(r.data);
      setLeaderboard(l.data.leaderboard || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const claimReward = async (type) => {
    setClaiming(type);
    try {
      let res;
      if (type === "first_booking") res = await api.post("/play/claim/first-booking");
      else if (type === "founding_daily") res = await api.post("/play/claim/founding-daily");
      toast.success(`+${res?.data?.tokens_awarded || 0} $Play earned!`);
      const s = await api.get("/play/summary");
      setSummary(s.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Claim failed");
    }
    setClaiming("");
  };

  if (loading) return <div className="flex justify-center items-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!summary) return null;

  const dailyPct = Math.round((summary.earned_today / summary.daily_cap) * 100);

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6" data-testid="play-wallet-page">
      {/* Hero Balance */}
      <div className="relative overflow-hidden rounded-lg bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 p-6 sm:p-8 text-white">
        <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-1">
            <Coins className="w-5 h-5" />
            <span className="text-sm font-heading uppercase tracking-wider opacity-80">$Play Balance</span>
          </div>
          <div className="flex items-baseline gap-3">
            <span className="text-4xl sm:text-5xl font-heading font-bold" data-testid="play-balance">{summary.balance.toLocaleString()}</span>
            <span className="text-lg opacity-70">$Play</span>
          </div>
          <p className="text-sm opacity-70 mt-1">= ₱{summary.balance_php.toLocaleString()} value</p>
          <div className="flex gap-6 mt-4">
            <div>
              <p className="text-xs opacity-60">Lifetime Earned</p>
              <p className="font-bold">{summary.lifetime_earned.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs opacity-60">Lifetime Spent</p>
              <p className="font-bold">{summary.lifetime_spent.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs opacity-60">Vesting</p>
              <p className="font-bold">{summary.vesting_tokens} ({summary.vesting_days}d)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Progress + Streak */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium">Today's Earnings</p>
              <span className="text-xs text-muted-foreground">{summary.earned_today}/{summary.daily_cap} cap</span>
            </div>
            <div className="h-3 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full transition-all" style={{ width: `${Math.min(dailyPct, 100)}%` }} />
            </div>
            <p className="text-xs text-muted-foreground mt-1">{summary.daily_remaining} $Play remaining today</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Flame className={`w-5 h-5 ${summary.streak.weeks > 0 ? "text-orange-500" : "text-muted-foreground"}`} />
              <p className="text-sm font-medium">Weekly Streak</p>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-heading font-bold" data-testid="streak-weeks">{summary.streak.weeks}</span>
              <span className="text-sm text-muted-foreground">week{summary.streak.weeks !== 1 ? "s" : ""}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {summary.streak.weeks > 0 ? `+${summary.streak.bonus_per_booking} bonus $Play per booking` : "Book this week to start a streak!"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Earn Opportunities */}
      <Card>
        <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><Gift className="w-4 h-4 text-amber-500" /> Earn $Play</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(summary.opportunities || []).map((opp, i) => {
            const Icon = EVENT_ICONS[opp.type] || Coins;
            return (
              <div key={i} className="flex items-center justify-between py-2.5 border-b last:border-0">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center">
                    <Icon className={`w-4 h-4 ${EVENT_COLORS[opp.type] || "text-amber-500"}`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{opp.label}</p>
                    <p className="text-xs text-muted-foreground">+{opp.tokens} $Play</p>
                  </div>
                </div>
                {opp.claimable ? (
                  <Button size="sm" onClick={() => claimReward(opp.type)} disabled={claiming === opp.type}
                    className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid={`claim-${opp.type}`}>
                    {claiming === opp.type ? "..." : "Claim"}
                  </Button>
                ) : (
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Earning Rules */}
      <Card>
        <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-green-600" /> How to Earn</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {rules && Object.entries(rules.rules).map(([key, rule]) => {
              const Icon = EVENT_ICONS[key] || Coins;
              return (
                <div key={key} className="bg-muted/50 rounded-lg p-3 text-center">
                  <Icon className={`w-5 h-5 mx-auto mb-1 ${EVENT_COLORS[key] || "text-primary"}`} />
                  <p className="text-lg font-heading font-bold text-primary">+{rule.tokens}</p>
                  <p className="text-[10px] font-medium">{rule.label}</p>
                  <p className="text-[9px] text-muted-foreground">{rule.desc}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Recent History */}
      <Card>
        <CardHeader><CardTitle className="font-heading text-base">Recent Activity</CardTitle></CardHeader>
        <CardContent>
          {(summary.recent_history || []).length === 0 ? (
            <p className="text-center text-muted-foreground py-4 text-sm">No activity yet. Start earning by booking a court!</p>
          ) : (
            <div className="space-y-2">
              {summary.recent_history.map(e => {
                const Icon = EVENT_ICONS[e.event_type] || Coins;
                const isEarn = e.amount > 0;
                return (
                  <div key={e.entry_id} className="flex items-center justify-between py-1.5 border-b last:border-0">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${isEarn ? "text-green-500" : "text-red-400"}`} />
                      <div>
                        <p className="text-sm capitalize">{(e.event_type || "").replace(/_/g, " ")}</p>
                        <p className="text-[10px] text-muted-foreground">{new Date(e.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <span className={`font-heading font-bold ${isEarn ? "text-green-600" : "text-red-500"}`}>
                      {isEarn ? <ArrowUpRight className="w-3 h-3 inline" /> : <ArrowDownRight className="w-3 h-3 inline" />}
                      {isEarn ? "+" : ""}{e.amount}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Leaderboard */}
      {leaderboard.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><Award className="w-4 h-4 text-amber-500" /> $Play Leaderboard</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {leaderboard.slice(0, 10).map((u, i) => (
                <div key={u.user_id} className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-bold w-5 ${i < 3 ? "text-amber-500" : "text-muted-foreground"}`}>#{i + 1}</span>
                    <span className="text-sm">{u.name}</span>
                    {(u.badges || []).includes("founding_player") && <Badge className="bg-amber-500 text-white text-[8px]">F</Badge>}
                  </div>
                  <span className="font-heading font-bold text-sm">{u.play_tokens.toLocaleString()} $P</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
